import { Commit, Dispatch } from 'vuex'
import { getApiKeys, generateApiKey, deleteApiKey as apiDeleteApiKey, getUsageSummary, getUsageByModel, getUsageBilling } from '@/utils/api' // Added deleteApiKey import
import { ApiKeyMetadata, GenerateApiKeyResponse, isApiError, NewApiKeyInfo, UsageSummary, UsageByModelRow, UsageTimeseriesRow, BillingModelRow, BillingModelBucketRow } from '@/types/api'

type MonthRange = { startISO: string; endISO: string; label: string };

function computeMonthRange(year: number, monthIndex0: number): MonthRange {
  const startMs = Date.UTC(year, monthIndex0, 1, 0, 0, 0, 0);
  const endMs = Date.UTC(year, monthIndex0 + 1, 1, 0, 0, 0, 0);
  const startISO = new Date(startMs).toISOString();
  const endISO = new Date(endMs).toISOString();
  const label = `${new Date(startMs).toLocaleString(undefined, { month: 'long', year: 'numeric' })}`;
  return { startISO, endISO, label };
}

export interface AccountState {
  apiKeys: ApiKeyMetadata[]
  newApiKeyInfo: NewApiKeyInfo | null // To temporarily store the newly generated key
  loading: boolean
  error: string | null
  // new for usage metrics
  selectedMonth: MonthRange | null
  usageSummary: UsageSummary | null
  usageModels: UsageByModelRow[]
  usageTimeseries: UsageTimeseriesRow[] // optional for future
  usageBillingByModel: BillingModelRow[]
  usageBillingByModelDay: BillingModelBucketRow[]
}

const initialState: AccountState = {
  apiKeys: [],
  newApiKeyInfo: null,
  loading: false,
  error: null,

  // usage metrics initial
  selectedMonth: null,
  usageSummary: null,
  usageModels: [],
  usageTimeseries: [],
  usageBillingByModel: [],
  usageBillingByModelDay: []
}

export const account = () => ({
  namespaced: true, // Important for module structure
  state: <AccountState>{
    ...initialState,
    loading: false, // Always reset loading/error on init
    error: null,
    newApiKeyInfo: null // Always clear new key on init
  },
  mutations: {
    setApiKeys(state: AccountState, keys: ApiKeyMetadata[]) {
      // Sort keys by creation date, newest first
      state.apiKeys = keys.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    },
    setNewApiKeyInfo(state: AccountState, info: NewApiKeyInfo | null) {
      state.newApiKeyInfo = info
      // Do NOT cache the new API key
    },
    setAccountLoading(state: AccountState, loading: boolean) {
      state.loading = loading
    },
    setAccountError(state: AccountState, error: string | null) {
      state.error = error
    },
    // usage metrics mutations
    setSelectedMonth(state: AccountState, month: MonthRange) {
      state.selectedMonth = month
    },
    setUsageSummary(state: AccountState, summary: UsageSummary | null) {
      state.usageSummary = summary
    },
    setUsageModels(state: AccountState, rows: UsageByModelRow[]) {
      state.usageModels = rows
    },
    setUsageBillingByModel(state: AccountState, rows: BillingModelRow[]) {
      state.usageBillingByModel = rows
    },
    setUsageBillingByModelDay(state: AccountState, rows: BillingModelBucketRow[]) {
      state.usageBillingByModelDay = rows
    }
  },
  actions: {
    async fetchApiKeys({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }) {
      commit('setAccountLoading', true)
      commit('setAccountError', null)

      const response = await getApiKeys()

      if (isApiError(response)) {
        console.error('Error fetching API keys:', response.message)
        commit('setAccountError', response.message)
        dispatch('updateSnackbar', { text: `API Keys Error: ${response.message}` }, { root: true })
        dispatch('addLogEvent', { title: 'API Keys Fetch Error', content: response }, { root: true })
        commit('setApiKeys', []) // Clear keys on error
      } else if (response.success) {
        commit('setApiKeys', response.apiKeys)
        dispatch(
          'addLogEvent',
          {
            title: 'API Keys Fetched',
            content: { count: response.apiKeys.length }
          },
          { root: true }
        )
      } else {
        const errorMsg = 'Failed to fetch API keys'
        commit('setAccountError', errorMsg)
        dispatch('updateSnackbar', { text: errorMsg }, { root: true })
        commit('setApiKeys', []) // Clear keys on failure
      }

      commit('setAccountLoading', false)
    },
    async generateNewApiKey({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }) {
      commit('setAccountLoading', true)
      commit('setAccountError', null)
      commit('setNewApiKeyInfo', null) // Clear previous new key info

      const response = await generateApiKey()

      if (isApiError(response)) {
        console.error('Error generating API key:', response.message)
        commit('setAccountError', response.message)
        dispatch('updateSnackbar', { text: `Generate Key Error: ${response.message}` }, { root: true })
        dispatch('addLogEvent', { title: 'API Key Generate Error', content: response }, { root: true })
      } else if (response.success) {
        // Commit the new key info to be displayed temporarily
        commit('setNewApiKeyInfo', { apiKey: response.apiKey, expiresAt: response.expiresAt })
        dispatch('updateSnackbar', { text: 'New API Key Generated Successfully!' }, { root: true })
        dispatch(
          'addLogEvent',
          {
            title: 'API Key Generated',
            content: { expiresAt: response.expiresAt } // Don't log the key itself
          },
          { root: true }
        )
        // Fetch the updated list of keys *after* successfully generating a new one
        await dispatch('fetchApiKeys')
      } else {
        const errorMsg = 'Failed to generate API key'
        commit('setAccountError', errorMsg)
        dispatch('updateSnackbar', { text: errorMsg }, { root: true })
      }

      commit('setAccountLoading', false)
    },
    // Action to clear the temporarily stored new API key after the user acknowledges it
    clearNewApiKey({ commit }: { commit: Commit }) {
      commit('setNewApiKeyInfo', null)
    },
    async deleteApiKey({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }, id: string) {
      commit('setAccountLoading', true)
      commit('setAccountError', null)

      const response = await apiDeleteApiKey(id) // Use the imported helper

      if (isApiError(response)) {
        console.error('Error deleting API key:', response.message)
        commit('setAccountError', response.message)
        dispatch('updateSnackbar', { text: `Delete Key Error: ${response.message}` }, { root: true })
        dispatch('addLogEvent', { title: 'API Key Delete Error', content: response }, { root: true })
      } else if (response.success) {
        dispatch('updateSnackbar', { text: 'API Key Deleted Successfully!' }, { root: true })
        dispatch(
          'addLogEvent',
          {
            title: 'API Key Deleted',
            content: { id } // Log the ID of the deleted key
          },
          { root: true }
        )
        // Refresh the list of keys after successful deletion
        await dispatch('fetchApiKeys')
      } else {
        const errorMsg = response.message || 'Failed to delete API key'
        commit('setAccountError', errorMsg)
        dispatch('updateSnackbar', { text: errorMsg }, { root: true })
      }

      commit('setAccountLoading', false)
    },

    async initSelectedMonthToCurrent({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }) {
      const now = new Date()
      const m = computeMonthRange(now.getUTCFullYear(), now.getUTCMonth())
      commit('setSelectedMonth', m)
      await dispatch('fetchUsageForSelectedMonth')
    },

    async changeSelectedMonth(
      { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
      { year, monthIndex0 }: { year: number; monthIndex0: number }
    ) {
      commit('setSelectedMonth', computeMonthRange(year, monthIndex0))
      await dispatch('fetchUsageForSelectedMonth')
    },

    async fetchUsageForSelectedMonth({ state, commit, dispatch }: { state: AccountState; commit: Commit; dispatch: Dispatch }) {
      if (!state.selectedMonth) return
      commit('setAccountLoading', true)
      commit('setAccountError', null)

      const { startISO, endISO } = state.selectedMonth

      const [summary, byModel, billingModel, billingModelDay] = await Promise.all([
        getUsageSummary({ start: startISO, end: endISO }),
        getUsageByModel({ start: startISO, end: endISO }),
        getUsageBilling({ start: startISO, end: endISO, groupBy: 'model' }),
        getUsageBilling({ start: startISO, end: endISO, groupBy: 'model:day' })
      ])

      function onError(where: string, err: any) {
        console.error(`Usage fetch error (${where}):`, err)
        const msg = typeof err?.message === 'string' ? err.message : 'Failed to fetch usage data'
        commit('setAccountError', msg)
        dispatch('updateSnackbar', { text: `Usage Error (${where})` }, { root: true })
        dispatch('addLogEvent', { title: `Usage Error (${where})`, content: err }, { root: true })
      }

      if (isApiError(summary)) onError('summary', summary)
      else if ((summary as any)?.success) commit('setUsageSummary', (summary as any).data as UsageSummary)
      else commit('setUsageSummary', null)

      if (isApiError(byModel)) onError('models', byModel)
      else if ((byModel as any)?.success) commit('setUsageModels', (byModel as any).data as UsageByModelRow[])
      else commit('setUsageModels', [])

      if (isApiError(billingModel)) onError('billing:model', billingModel)
      else if ((billingModel as any)?.success) commit('setUsageBillingByModel', (billingModel as any).data as BillingModelRow[])
      else commit('setUsageBillingByModel', [])

      if (isApiError(billingModelDay)) onError('billing:model:day', billingModelDay)
      else if ((billingModelDay as any)?.success) commit('setUsageBillingByModelDay', (billingModelDay as any).data as BillingModelBucketRow[])
      else commit('setUsageBillingByModelDay', [])

      commit('setAccountLoading', false)
    }
  },
  getters: {
    accountApiKeys: (state: AccountState): ApiKeyMetadata[] => state.apiKeys,
    accountNewApiKeyInfo: (state: AccountState): NewApiKeyInfo | null => state.newApiKeyInfo,
    accountLoading: (state: AccountState): boolean => state.loading,
    accountError: (state: AccountState): string | null => state.error,
    // usage metrics getters
    accountSelectedMonth: (state: AccountState): MonthRange | null => state.selectedMonth,
    accountUsageSummary: (state: AccountState): UsageSummary | null => state.usageSummary,
    accountUsageModels: (state: AccountState): UsageByModelRow[] => state.usageModels,
    accountUsageBillingByModel: (state: AccountState): BillingModelRow[] => state.usageBillingByModel,
    accountUsageBillingByModelDay: (state: AccountState): BillingModelBucketRow[] => state.usageBillingByModelDay
  }
})
